import { IFormField } from '@ViewModels';
import { UseFormSetValue } from 'react-hook-form';

/**
 * After a successful survey save, copy the server-assigned `_type` discriminator onto any
 * locally-added fields that don't already have it. Locally-added questions begin life
 * without `_type` set; the server assigns it on save. Because `hasBeenSaved` in the
 * "Not Saved" indicator is `!!field._type && !isClone`, leaving `_type` undefined leaves the
 * indicator stuck on after a successful save.
 *
 * IMPORTANT: This patches in place by id — it does NOT replace the field array wholesale.
 * The PUT/POST response strips `options` from each field (those only come back via
 * `?expand=options` on the GET), so overwriting `customForm.fields` with the server response
 * would wipe dropdown choices and orphan any visibility rules that reference those options.
 * Patch `_type` only and leave the rest of the local field intact.
 *
 * No-ops cheaply when nothing needs patching, so it's safe to call unconditionally from
 * mutation onSuccess handlers.
 */
export const patchFieldTypesFromServerResponse = <TFormData extends { customForm?: { fields?: IFormField<string>[] } }>(
	serverFields: IFormField<string>[] | undefined,
	currentFields: IFormField<string>[] | undefined,
	setValue: UseFormSetValue<TFormData>
): void => {
	if (!serverFields?.length || !currentFields?.length) return;

	const serverTypesById = new Map<string, IFormField<string>['_type']>();
	for (const f of serverFields) {
		if (f.id && f._type) serverTypesById.set(f.id, f._type);
	}
	if (serverTypesById.size === 0) return;

	let mutated = false;
	const patched = currentFields.map(field => {
		if (field._type) return field;
		const serverType = serverTypesById.get(field.id);
		if (!serverType) return field;
		mutated = true;
		return { ...field, _type: serverType };
	});
	if (mutated) {
		setValue('customForm.fields' as never, patched as never);
	}
};
